import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from './AuthContext';
import { Link, useLocation } from 'react-router-dom';

const Header = () => {
  const { user, signOut } = useAuth();
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [activeIndicator, setActiveIndicator] = useState({ left: 0, width: 0 });
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const navRef = useRef(null);
  const location = useLocation();

  // Navigation items with their paths (Home removed - logo click goes to home)
  const navItems = [
    { path: '/specialisms', label: 'Specialisms' },
    { path: '/jobs', label: 'Jobs' },
    { path: '/blogs', label: 'Blogs' },
    { path: '/employers', label: 'Employers' },
    { path: '/candidates', label: 'Candidates' },
    { path: '/about', label: 'About Us' }
  ];

  useEffect(() => {
    const handleScroll = () => {
      const scrollTop = window.scrollY;
      setIsScrolled(scrollTop > 100); // Trigger after 100px of scroll
    };

    const handleResize = () => {
      // Recalculate indicator position on window resize
      if (navRef.current) {
        const activeNavItem = navRef.current.querySelector(`[data-path="${location.pathname}"]`);
        if (activeNavItem) {
          // Force reflow
          void navRef.current.offsetHeight;
          
          const navRect = navRef.current.getBoundingClientRect();
          const itemRect = activeNavItem.getBoundingClientRect();
          setActiveIndicator({
            left: itemRect.left - navRect.left,
            width: itemRect.width
          });
        }
      }
    };

    const handleTransitionEnd = () => {
      // Recalculate after CSS transitions complete
      setTimeout(handleResize, 10);
    };

    window.addEventListener('scroll', handleScroll);
    window.addEventListener('resize', handleResize);
    
    // Listen for transition end events on the header
    const headerElement = document.querySelector('header');
    if (headerElement) {
      headerElement.addEventListener('transitionend', handleTransitionEnd);
    }
    
    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', handleResize);
      if (headerElement) {
        headerElement.removeEventListener('transitionend', handleTransitionEnd);
      }
    };
  }, [location.pathname]);

  // Update active indicator position when location changes
  useEffect(() => {
    const updateIndicatorPosition = () => {
      if (navRef.current) {
        // Hide indicator when on home page since there's no Home tab
        if (location.pathname === '/') {
          setActiveIndicator({ left: 0, width: 0 });
          return;
        }
        
        const activeNavItem = navRef.current.querySelector(`[data-path="${location.pathname}"]`);
        if (activeNavItem) {
          // Force a reflow to ensure accurate measurements
          void navRef.current.offsetHeight;
          
          const navRect = navRef.current.getBoundingClientRect();
          const itemRect = activeNavItem.getBoundingClientRect();
          setActiveIndicator({
            left: itemRect.left - navRect.left,
            width: itemRect.width
          });
        } else {
          // No matching nav item found, hide indicator
          setActiveIndicator({ left: 0, width: 0 });
        }
      }
    };

    // Use requestAnimationFrame for more accurate timing
    const rafId = requestAnimationFrame(() => {
      updateIndicatorPosition();
    });
    
    return () => cancelAnimationFrame(rafId);
  }, [location.pathname]);

  // Close mobile menu when route changes
  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location.pathname]);

  const handleSignOut = async () => {
    await signOut();
    setShowUserMenu(false);
    setIsMobileMenuOpen(false);
  };

  const closeMobileMenu = () => {
    setIsMobileMenuOpen(false);
  };

  const isActiveLink = (path) => {
    // Don't highlight any nav item when on home page since there's no Home tab
    if (location.pathname === '/') return false;
    if (path !== '/' && location.pathname.startsWith(path)) return true;
    return false;
  };

  const getLinkClasses = (path) => {
    const baseClasses = "text-gray-700 hover:text-brand-blue transition-all duration-300 font-medium whitespace-nowrap relative group pb-2 px-3 py-2";
    const activeClasses = "text-brand-blue";
    return isActiveLink(path) ? `${baseClasses} ${activeClasses}` : baseClasses;
  };

  // Check if we're on a form page
  const isFormPage = location.pathname === '/find-jobs' || location.pathname === '/hire-talent';

  return (
    <header className="fixed top-0 left-0 right-0 bg-white z-50 shadow-sm border-b border-gray-200">
      <div className="container mx-auto px-6 py-3">
        {isFormPage ? (
          // Form page header layout
          <div className="flex items-center justify-between">
            {/* Left Side - Back Button and Logo */}
            <div className="flex items-center gap-4">
              <Link
                to="/"
                className="text-gray-700 hover:text-brand-blue flex items-center gap-2 bg-gray-100 hover:bg-gray-200 px-3 py-2 rounded-full transition-all duration-300"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                </svg>
              </Link>
              <img 
                src="/logo_transparent.png" 
                alt="JCR Logo" 
                className="h-8 w-auto"
              />
            </div>

            {/* Center Content - Title */}
            <div className="flex-1 flex justify-center">
              <h1 className="text-lg font-light text-gray-900 text-center">
                {location.pathname === '/find-jobs' ? 'Apply for Position' : 'Hire Talent'}
              </h1>
            </div>

            {/* Right Spacer (to balance the layout) */}
            <div className="w-32"></div>
          </div>
        ) : (
          // Regular page header layout
          <div className="flex items-center justify-between">
            {/* Logo */}
            <div className="flex justify-start flex-shrink-0">
              <Link to="/" className="flex items-center" onClick={closeMobileMenu}>
                <img 
                  src="/logo_transparent.png" 
                  alt="JCR Logo" 
                  className="h-8 md:h-10 w-auto"
                />
              </Link>
            </div>
            
            {/* Desktop Navigation - Centered with fluid indicator */}
            <nav 
              ref={navRef}
              className="hidden lg:flex items-center justify-center relative flex-1"
              style={{ gap: '8px' }}
            >
              {/* Fluid background indicator */}
              <div 
                className="absolute bottom-0 rounded-full transition-all duration-700 ease-out transform"
                style={{
                  left: `${activeIndicator.left}px`,
                  width: `${activeIndicator.width}px`,
                  height: '3px',
                  background: 'linear-gradient(90deg, #001f3e, #002a54, #001f3e)',
                  boxShadow: '0 0 20px rgba(0, 31, 62, 0.4), 0 0 40px rgba(0, 31, 62, 0.2)',
                  borderRadius: '50px',
                  opacity: activeIndicator.width > 0 ? 1 : 0
                }}
              >
                {/* Liquid ripple effect */}
                <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white to-transparent opacity-20 rounded-full animate-pulse"></div>
              </div>

              {navItems.map((item) => (
                <Link 
                  key={item.path}
                  to={item.path} 
                  data-path={item.path}
                  className={getLinkClasses(item.path)}
                  style={{ position: 'relative', zIndex: 1 }}
                >
                  <span className="relative z-10">{item.label}</span>
                  {/* Hover ripple effect */}
                  <div className="absolute inset-0 rounded-full bg-gradient-to-r from-brand-blue/10 via-brand-blue/15 to-brand-blue/10 opacity-0 group-hover:opacity-100 transition-all duration-300 scale-0 group-hover:scale-100"></div>
                </Link>
              ))}
            </nav>
            
            {/* Desktop Action Buttons */}
            <div className="hidden lg:flex items-center justify-end space-x-4 flex-shrink-0">
              {user ? (
                <div className="flex items-center space-x-4">
                  <Link
                    to="/hire-talent"
                    className="text-gray-700 hover:text-brand-blue transition-colors font-medium"
                  >
                    Hire talent
                  </Link>
                  <Link
                    to="/find-jobs"
                    className="bg-brand-blue hover:bg-blue-700 text-white px-6 py-2 rounded-full transition-all duration-300 font-medium"
                  >
                    Find Jobs
                  </Link>
                  <div className="relative">
                    <button
                      onClick={() => setShowUserMenu(!showUserMenu)}
                      className="flex items-center ml-4 space-x-2 text-gray-700 hover:text-brand-blue transition-colors"
                    >
                      <div className="bg-brand-blue w-8 h-8 rounded-full flex items-center justify-center">
                        <span className="text-sm font-medium text-white">
                          {user.user_metadata?.firstName?.charAt(0) || user.email?.charAt(0) || 'U'}
                        </span>
                      </div>
                      <span className="font-medium">
                        {user.user_metadata?.firstName || 'User'}
                      </span>
                      <svg className="w-4 h-4 fill-none stroke-current" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    </button>

                    {showUserMenu && (
                      <div className="absolute right-0 mt-2 w-48 bg-white rounded-md shadow-lg py-1 z-50">
                        <Link
                          to="/profile"
                          onClick={() => setShowUserMenu(false)}
                          className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                        >
                          Profile Settings
                        </Link>
                        <button
                          onClick={handleSignOut}
                          className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                        >
                          Sign Out
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              ) : (
                <div className="flex items-center space-x-4">
                  <Link
                    to="/hire-talent"
                    className="text-gray-700 hover:text-brand-blue transition-colors font-medium"
                  >
                    Hire talent
                  </Link>
                  <Link
                    to="/find-jobs"
                    className="bg-brand-blue hover:bg-blue-700 text-white px-6 py-2 rounded-full transition-all duration-300 font-medium"
                  >
                    Find Jobs
                  </Link>
                </div>
              )}
              
              {/* Discreet Admin Access */}
              <Link
                to="/admin-login"
                className="text-xs text-gray-400 hover:text-gray-600 opacity-50 hover:opacity-100 transition-all duration-300"
                title="Admin Access"
              >
                •
              </Link>
            </div>

            {/* Mobile Menu Button */}
            <div className="flex items-center space-x-2 lg:hidden">
              {/* Mobile Action Buttons - Show only when menu is closed */}
              {!isMobileMenuOpen && (
                <>
                  {user ? (
                    <Link
                      to="/find-jobs"
                      className="bg-brand-blue hover:bg-blue-700 text-white px-4 py-2 rounded-full transition-all duration-300 font-medium text-sm"
                    >
                      Find Jobs
                    </Link>
                  ) : (
                    <Link
                      to="/find-jobs"
                      className="bg-brand-blue hover:bg-blue-700 text-white px-4 py-2 rounded-full transition-all duration-300 font-medium text-sm"
                    >
                      Find Jobs
                    </Link>
                  )}
                </>
              )}
              
              {/* Burger Menu Icon */}
              <button
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                className="p-2 text-gray-700 hover:text-brand-blue transition-colors focus:outline-none"
                aria-label="Toggle menu"
              >
                {isMobileMenuOpen ? (
                  // Close icon (X)
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                ) : (
                  // Hamburger icon
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                  </svg>
                )}
              </button>
            </div>
          </div>
        )}

        {/* Mobile Menu Dropdown */}
        {isMobileMenuOpen && (
          <div className="lg:hidden border-t border-gray-200 bg-white">
            <nav className="px-6 py-4 space-y-1">
              {navItems.map((item) => (
                <Link
                  key={item.path}
                  to={item.path}
                  onClick={closeMobileMenu}
                  className={`block px-4 py-3 rounded-lg transition-colors ${
                    isActiveLink(item.path)
                      ? 'bg-brand-blue/10 text-brand-blue font-medium'
                      : 'text-gray-700 hover:bg-gray-100'
                  }`}
                >
                  {item.label}
                </Link>
              ))}
              
              {/* Mobile Action Buttons */}
              <div className="pt-4 mt-4 border-t border-gray-200 space-y-2">
                <Link
                  to="/hire-talent"
                  onClick={closeMobileMenu}
                  className="block px-4 py-3 rounded-lg text-gray-700 hover:bg-gray-100 transition-colors font-medium"
                >
                  Hire talent
                </Link>
                {user ? (
                  <>
                    <Link
                      to="/profile"
                      onClick={closeMobileMenu}
                      className="block px-4 py-3 rounded-lg text-gray-700 hover:bg-gray-100 transition-colors"
                    >
                      Profile Settings
                    </Link>
                    <button
                      onClick={handleSignOut}
                      className="block w-full text-left px-4 py-3 rounded-lg text-gray-700 hover:bg-gray-100 transition-colors"
                    >
                      Sign Out
                    </button>
                  </>
                ) : null}
                <Link
                  to="/admin-login"
                  onClick={closeMobileMenu}
                  className="block px-4 py-3 rounded-lg text-gray-400 hover:bg-gray-100 transition-colors text-sm"
                >
                  Admin Access
                </Link>
              </div>
            </nav>
          </div>
        )}
      </div>
    </header>
  );
};

export default Header; 